import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faPaperclip } from "@fortawesome/free-solid-svg-icons";

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const FullScreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: ${props => props.isOpen ? 'block' : 'none'};
  animation: ${props => props.isOpen ? fadeIn : 'none'} 0.2s ease-out;
`;

const DetailContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  overflow-y: auto;
  z-index: 10000;
  animation: ${props => props.isOpen ? slideUp : 'none'} 0.3s ease-out;
  
  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
    
    &:hover {
      background: #a8a8a8;
    }
  }
`;

const DetailHeader = styled.div`
  position: sticky;
  top: 0;
  background: white;
  border-bottom: 1px solid #e9ecef;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const DetailTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: #666;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  
  &:hover {
    background: #f0f0f0;
    color: #333;
  }
`;

const DetailContent = styled.div`
  padding: 20px;
`;

const FieldGroup = styled.div`
  margin-bottom: 24px;
`;

const FieldLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #555;
  margin-bottom: 8px;
  padding-bottom: 4px;
  border-bottom: 2px solid #f0f0f0;
`;

const FieldValue = styled.div`
  font-size: 16px;
  color: #333;
  line-height: 1.5;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  word-wrap: break-word;
  white-space: pre-wrap;
  
  &.empty {
    color: #999;
    font-style: italic;
  }
`;

const MobileDetailView = ({ 
  isOpen, 
  onClose, 
  board = {}, 
  columns = [],
  codeDisplayMap = {}
}) => {
  // 팝업 열릴 때 배경 스크롤 비활성화
  useEffect(() => {
    if (isOpen) {
      // 현재 스크롤 위치 저장하고 body 스크롤 막기
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
    } else {
      // 스크롤 위치 복원
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }

    return () => {
      // 클린업 - 컴포넌트 언마운트시 스크롤 복원
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // 표시할 컬럼들 필터링 (visible한 컬럼들만)
  const visibleColumns = columns.filter(col => col.cell_visible);

  const renderFieldValue = (column, value) => {
    if (!value && value !== 0 && value !== false) {
      return <FieldValue className="empty">내용 없음</FieldValue>;
    }

    let displayValue = value;

    // 코드 매핑 처리
    if (["S", "M", "C"].includes(column.bind_type) && codeDisplayMap[column.bind_key]) {
      displayValue = codeDisplayMap[column.bind_key][value] || value;
    }

    // 마스크 처리
    if (column.mask === "PriceNum") {
      displayValue = displayValue ? displayValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "";
    } else if (column.mask === "Files") {
      return (
        <FieldValue>
          {value ? (
            <>
              <FontAwesomeIcon icon={faPaperclip} style={{ color: '#4fc3f7', marginRight: '8px' }} />
              파일 첨부됨
            </>
          ) : "첨부파일 없음"}
        </FieldValue>
      );
    }

    // 체크박스 처리
    if (column.control_type === "CheckBox") {
      displayValue = (value === "true" || value === true) ? "예" : "아니오";
    }

    return <FieldValue>{displayValue}</FieldValue>;
  };

  return (
    <>
      <FullScreenOverlay isOpen={isOpen} onClick={onClose} />
      <DetailContainer isOpen={isOpen}>
        <DetailHeader>
          <DetailTitle>상세보기</DetailTitle>
          <CloseButton onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </DetailHeader>
        
        <DetailContent>
          {visibleColumns.map((column) => (
            <FieldGroup key={column.column_id}>
              <FieldLabel>
                {column.mapping_name || column.column_title}
              </FieldLabel>
              {renderFieldValue(column, board[column.column_id])}
            </FieldGroup>
          ))}
        </DetailContent>
      </DetailContainer>
    </>
  );
};

export default MobileDetailView;